import { getEnvParam } from "@/env";

export type TravelMode = "driving" | "public_transport" | "walking";

export interface Anchor {
    id: string;
    lat: number;
    lng: number;
    modes: TravelMode[];
}

export interface IsochroneFeatureProps {
    anchor_id: string;
    mode: TravelMode;
    minutes: number;
}

export interface FailedIsochrone {
    anchor_id: string;
    mode: TravelMode;
    minutes: number;
    message: string;
}

export interface IsochroneResponse {
    geojson: GeoJSON.FeatureCollection<GeoJSON.MultiPolygon, IsochroneFeatureProps>;
    failed: FailedIsochrone[];
}

export async function postIsochrones(
    anchors: Anchor[],
): Promise<IsochroneResponse> {
    const result = await fetch(
        `${process.env.BUN_PUBLIC_TILE_SERVER}/api/isochrones?${getEnvParam()}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-API-KEY": process.env.BUN_PUBLIC_TILE_SERVER_API_KEY,
            },
            body: JSON.stringify({ anchors }),
        },
    );
    if (!result.ok) {
        throw new Error(
            result.status === 429
                ? "Rate limited by the isochrone provider — wait a minute and try again."
                : `Failed to fetch isochrones: ${result.status}`,
        );
    }
    return result.json();
}

const MODE_CODES: Record<TravelMode, string> = {
    driving: "d",
    public_transport: "p",
    walking: "w",
};

const CODE_MODES: Record<string, TravelMode> = {
    d: "driving",
    p: "public_transport",
    w: "walking",
};

/**
 * Round to 4 dp to match the server's isochrone cache quantization, so the
 * URL param, the /api/isochrones request and the tile-request anchors all
 * agree on the exact same coordinates.
 */
export function roundCoord(value: number): number {
    return Math.round(value * 1e4) / 1e4;
}

/** URL codec: "lat_lng_modes;lat_lng_modes" with modes ⊆ "dpw". */
export function serializeAnchors(anchors: Anchor[]): string {
    return anchors
        .map(
            (a) =>
                `${roundCoord(a.lat)}_${roundCoord(a.lng)}_${a.modes
                    .map((m) => MODE_CODES[m])
                    .join("")}`,
        )
        .join(";");
}

export function parseAnchors(param: string | null): Anchor[] {
    if (!param) return [];
    const anchors: Anchor[] = [];
    for (const part of param.split(";")) {
        const [latRaw, lngRaw, modesRaw] = part.split("_");
        const lat = parseFloat(latRaw ?? "");
        const lng = parseFloat(lngRaw ?? "");
        if (isNaN(lat) || isNaN(lng)) continue;
        const modes = [...(modesRaw ?? "")]
            .map((code) => CODE_MODES[code])
            .filter((m): m is TravelMode => !!m);
        if (modes.length === 0) continue;
        anchors.push({
            id: `a${anchors.length}`,
            lat: roundCoord(lat),
            lng: roundCoord(lng),
            modes,
        });
    }
    return anchors;
}
